/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from "moment-timezone";
import React, { useState } from "react";
import { Edit, Delete } from "@mui/icons-material";
import { Box, Typography, IconButton, Chip } from "@mui/material";

import Panel from "../../../components/Panel";
import OkhatiDialog from "../../../components/Dialog/Dialog";
import { IAttendancePunch } from "../../../interfaces/AttendanceInterface";
import { useDeleteAttendancePunch } from "../../../hooks/query/useAttendancePunches";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import { DEFAULT_TIMEZONE } from "../../../constants/resourceCentre";
import { AttendanceDutyType } from "../../../constants/attendance";

interface AttendancePunchInfoProps {
  punch: IAttendancePunch;
  onClose: () => void;
  onEdit: (punch: IAttendancePunch) => void;
}

function DetailRow({ label, value, sx }: { label: string; value: React.ReactNode; sx?: any }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline" }}>
      <Typography sx={{ width: "30%", flexShrink: 0 }} fontWeight={600}>
        {label}
      </Typography>
      {typeof value === "string" || typeof value === "number" ? (
        <Typography sx={sx}>{value}</Typography>
      ) : (
        <Box sx={sx}>{value}</Box>
      )}
    </Box>
  );
}

function getDutyTypeChipColor(dutyType?: string): "warning" | "info" | "default" {
  if (dutyType === AttendanceDutyType.OVERTIME) return "warning";
  if (dutyType === AttendanceDutyType.ON_CALL) return "info";
  return "default";
}

export default function AttendancePunchInfo({
  punch,
  onClose,
  onEdit
}: AttendancePunchInfoProps): React.JSX.Element {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { mutate: deletePunchMutation } = useDeleteAttendancePunch();

  const handleDelete = () => {
    deletePunchMutation(punch.id as number, {
      onSuccess: (response) => {
        if (response.success) {
          onClose();
        }
      }
    });
  };

  const user = punch.employee || punch.serviceProvider;
  const name = user ? `${user.firstName || ""} ${user.lastName || ""}` : "Unknown";

  const titleJsx = (
    <Box
      sx={{ width: "100%" }}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      pb={1}
    >
      <Box display="flex" sx={{ alignItems: "center" }}>
        <Typography>
          <Box component="span" fontWeight="600" fontSize="16px" pr={2}>
            Punch Information
          </Box>
        </Typography>
      </Box>
      <Box display="flex">
        <IconButton
          sx={{ cursor: "pointer", padding: "5px" }}
          onClick={() => setDeleteDialogOpen(true)}
        >
          <Delete />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "baseline" }} ml="8px">
          <IconButton sx={{ cursor: "pointer", padding: "5px" }} onClick={() => onEdit(punch)}>
            <Edit />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Panel titleJsx={titleJsx} onClose={onClose}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <DetailRow
          label="Staff Name"
          value={<Typography sx={{ textTransform: "capitalize" }}>{name}</Typography>}
        />
        <DetailRow
          label="User Type"
          value={<Typography>{punch.employeeId ? "Employee" : "Service Provider"}</Typography>}
        />
        <DetailRow
          label="Punch Time"
          value={
            <Box>
              <Typography>
                {convertADtoBS(moment.tz(punch.punchedAt, DEFAULT_TIMEZONE).toDate()).formatted2}
              </Typography>
              <Typography sx={{ textTransform: "uppercase" }}>
                {moment.tz(punch.punchedAt, DEFAULT_TIMEZONE).format("YYYY-MM-DD hh:mm a")}
              </Typography>
            </Box>
          }
        />
        <DetailRow
          label="Type"
          value={
            <Chip
              label={punch.punchType}
              size="small"
              color={punch.punchType.toLowerCase().includes("in") ? "success" : "error"}
              sx={{ fontWeight: 600, textTransform: "uppercase" }}
            />
          }
        />
        <DetailRow
          label="Duty Type"
          value={
            <Chip
              label={(punch.dutyType || AttendanceDutyType.REGULAR).replace(/_/g, " ")}
              size="small"
              variant="outlined"
              color={getDutyTypeChipColor(punch.dutyType)}
              sx={{ fontWeight: 600, textTransform: "capitalize" }}
            />
          }
        />
        <DetailRow
          label="Source"
          value={<Typography sx={{ textTransform: "capitalize" }}>{punch.punchSource}</Typography>}
        />
        {punch.biometricDeviceId && (
          <DetailRow
            label="Biometric Device ID"
            value={<Typography>{punch.biometricDeviceId}</Typography>}
          />
        )}
        {punch.deviceLogId && (
          <DetailRow label="Device Log ID" value={<Typography>{punch.deviceLogId}</Typography>} />
        )}
        <DetailRow
          label="Created At"
          value={
            <Typography>
              {convertADtoBS(moment(punch.created_at).toDate()).formatted4}{" "}
              {moment(punch.created_at).format("hh:mm A")}
            </Typography>
          }
        />
        {punch.updated_at && punch.updated_at !== punch.created_at && (
          <DetailRow
            label="Updated At"
            value={
              <Typography>
                {convertADtoBS(moment(punch.updated_at).toDate()).formatted4}{" "}
                {moment(punch.updated_at).format("hh:mm A")}
              </Typography>
            }
          />
        )}
      </Box>

      {deleteDialogOpen && (
        <OkhatiDialog
          title="Delete Punch"
          description={`Are you sure you want to delete this punch entry for ${name}?`}
          next={handleDelete}
          cancel={() => setDeleteDialogOpen(false)}
          readMode={false}
        />
      )}
    </Panel>
  );
}
